import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Package, Users, Truck, Car, FileBarChart2,
  LogOut, ClipboardList, PlusCircle, ListChecks, Settings as SettingsIcon,
  Map as MapIcon, BookMarked, UserCog, Menu, MoreHorizontal, Receipt, Zap, Coins,
} from "lucide-react";
import PushBell from "@/components/PushBell";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV_BY_ROLE = {
  admin: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-admin-dashboard" },
    { to: "/admin/orders", label: "Aufträge", icon: Package, testid: "nav-admin-orders" },
    { to: "/admin/map", label: "Karte", icon: MapIcon, testid: "nav-admin-map" },
    { to: "/admin/customers", label: "Kunden", icon: Users, testid: "nav-admin-customers" },
    { to: "/admin/billing", label: "Abrechnung", icon: Receipt, testid: "nav-admin-billing" },
    { to: "/admin/users", label: "Benutzer", icon: UserCog, testid: "nav-admin-users" },
    { to: "/admin/address-book", label: "Adressbuch", icon: BookMarked, testid: "nav-admin-addressbook" },
    { to: "/admin/templates", label: "Vorlagen", icon: Zap, testid: "nav-admin-templates" },
    { to: "/admin/drivers", label: "Fahrer", icon: Truck, testid: "nav-admin-drivers" },
    { to: "/admin/vehicles", label: "Fahrzeuge", icon: Car, testid: "nav-admin-vehicles" },
    { to: "/admin/reports", label: "Reports", icon: FileBarChart2, testid: "nav-admin-reports" },
    { to: "/admin/settings", label: "Einstellungen", icon: SettingsIcon, testid: "nav-admin-settings" },
  ],
  dispatcher: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dispatcher-dashboard" },
    { to: "/admin/orders", label: "Aufträge", icon: Package, testid: "nav-dispatcher-orders" },
    { to: "/admin/customers", label: "Kunden", icon: Users, testid: "nav-dispatcher-customers" },
    { to: "/admin/address-book", label: "Adressbuch", icon: BookMarked, testid: "nav-dispatcher-addressbook" },
    { to: "/admin/drivers", label: "Fahrer", icon: Truck, testid: "nav-dispatcher-drivers" },
    { to: "/admin/reports", label: "Reports", icon: FileBarChart2, testid: "nav-dispatcher-reports" },
  ],
  customer: [
    { to: "/portal", label: "Übersicht", icon: LayoutDashboard, end: true, testid: "nav-customer-dashboard" },
    { to: "/portal/orders", label: "Meine Aufträge", icon: ClipboardList, testid: "nav-customer-orders" },
    { to: "/portal/orders/new", label: "Neuer Auftrag", icon: PlusCircle, testid: "nav-customer-create" },
    { to: "/portal/address-book", label: "Adressbuch", icon: BookMarked, testid: "nav-customer-addressbook" },
  ],
  driver: [
    { to: "/driver", label: "Meine Touren", icon: ListChecks, end: true, testid: "nav-driver-tasks" },
    { to: "/driver/payouts", label: "Vergütungen", icon: Coins, testid: "nav-driver-payouts" },
  ],
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const navBase = NAV_BY_ROLE[user?.role] || [];
  // Filter: hide "Vergütungen" item for drivers with hide_payout flag
  const nav = user?.role === "driver" && user?.hide_payout
    ? navBase.filter((n) => n.to !== "/driver/payouts")
    : navBase;
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Bottom-nav: up to 4 primary items + "Mehr" overflow when there are more than 4
  const bottomNav = nav.slice(0, 4);
  const overflowNav = nav.slice(4);
  // Show bottom-nav only on mobile/tablet AND only when more than 1 nav item exists.
  // This drives a CSS var so the main padding + sticky CTAs collapse the reserved
  // 56px slot for sub-drivers (single nav item).
  const hasBottomNav = nav.length > 1;

  const NavItem = ({ item, onClick, dark }) => {
    const { to, label, icon: Icon, end, testid } = item;
    return (
      <NavLink
        to={to}
        end={end}
        onClick={onClick}
        data-testid={testid}
        className={({ isActive }) =>
          `flex items-center gap-3 px-5 py-3 text-sm transition-colors border-l-2 ${
            isActive
              ? (dark ? "text-white bg-white/[0.08] border-l-[var(--gold)]" : "text-[var(--text)] bg-[var(--surface-2)] border-l-[var(--gold)]")
              : (dark ? "text-white/65 hover:text-white hover:bg-white/[0.04] border-transparent" : "text-[var(--text-2)] hover:text-[var(--text)] hover:bg-[var(--surface-2)] border-transparent")
          }`
        }
      >
        <Icon size={16} strokeWidth={1.5} />
        <span>{label}</span>
      </NavLink>
    );
  };

  return (
    <div
      className="min-h-screen flex"
      style={{
        background: "var(--bg)",
        color: "var(--text)",
        // 56px reserved when bottom-nav is rendered (mobile <1024px). Desktop sidebar
        // ignores this via media query in index.css.
        "--bottom-nav-h": hasBottomNav ? "56px" : "0px",
      }}
    >
      {/* Sidebar — Tannengrün (desktop only ≥1024px) */}
      <aside
        className="hidden lg:flex flex-col w-64 text-white shrink-0"
        style={{ background: "var(--header)", borderRight: "1px solid var(--header-2)" }}
      >
        <div className="px-6 py-7 border-b border-white/10">
          <img src="/branding/nivara_gold.png" alt="NIVARA" className="h-7 w-auto object-contain" />
          <div className="label-eyebrow mt-2" style={{ color: "rgba(255,255,255,0.55)" }}>Transport Portal</div>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {nav.map((item) => (
            <NavItem key={item.to} item={item} dark />
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-sm text-white font-medium truncate" data-testid="user-name">{user?.name}</div>
          <div className="text-xs text-white/50 truncate">{user?.email}</div>
          <div className="label-eyebrow mt-2" style={{ color: "var(--gold)" }}>{user?.role}</div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="mt-4 w-full flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-wider border border-white/20 hover:border-white/50 text-white/80 hover:text-white rounded-sm transition-colors"
          >
            <LogOut size={14} strokeWidth={1.5} /> Abmelden
          </button>
          <div className="mt-3 flex justify-center">
            <PushBell />
          </div>
        </div>
      </aside>

      {/* Top bar (Mobile + Tablet, <1024px) — Tannengrün */}
      <div
        className="lg:hidden fixed top-0 inset-x-0 z-40 backdrop-blur px-3 py-2.5 flex items-center justify-between text-white"
        style={{
          background: "rgba(31,58,46,0.97)",
          borderBottom: "1px solid var(--header-2)",
          paddingTop: "calc(env(safe-area-inset-top) + 0.625rem)",
        }}
      >
        <div className="flex items-center gap-2">
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-button"
                aria-label="Menü öffnen"
                className="p-2 -ml-1 rounded-sm hover:bg-white/10"
              >
                <Menu size={22} strokeWidth={1.5} />
              </button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="p-0 w-72 border-0 text-white"
              style={{ background: "var(--header)" }}
            >
              <div className="px-6 py-7 border-b border-white/10">
                <img src="/branding/nivara_gold.png" alt="NIVARA" className="h-7 w-auto object-contain" />
                <div className="label-eyebrow mt-2" style={{ color: "rgba(255,255,255,0.55)" }}>Transport Portal</div>
              </div>
              <nav className="py-3">
                {nav.map((item) => (
                  <NavItem
                    key={item.to}
                    item={item}
                    dark
                    onClick={() => setDrawerOpen(false)}
                  />
                ))}
              </nav>
              <div className="p-4 mt-2 border-t border-white/10">
                <div className="text-sm text-white font-medium truncate">{user?.name}</div>
                <div className="text-xs text-white/50 truncate">{user?.email}</div>
                <div className="label-eyebrow mt-2" style={{ color: "var(--gold)" }}>{user?.role}</div>
                <button
                  onClick={() => { setDrawerOpen(false); handleLogout(); }}
                  data-testid="drawer-logout-button"
                  className="mt-4 w-full flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-wider border border-white/20 hover:border-white/50 text-white/80 hover:text-white rounded-sm transition-colors"
                >
                  <LogOut size={14} strokeWidth={1.5} /> Abmelden
                </button>
              </div>
            </SheetContent>
          </Sheet>
          <img src="/branding/nivara_gold.png" alt="NIVARA" className="h-5 w-auto" />
        </div>
        <div className="flex items-center gap-2">
          <PushBell />
          <button
            onClick={handleLogout}
            data-testid="mobile-logout-button"
            className="text-[10px] uppercase tracking-wider text-white/70 hover:text-white px-2 py-1"
          >
            Abmelden
          </button>
        </div>
      </div>

      {/* Bottom nav (Mobile + Tablet, <1024px) — Tannengrün, 4 + "Mehr" if more */}
      {hasBottomNav && (
        <nav
          className="lg:hidden fixed bottom-0 inset-x-0 z-40 backdrop-blur grid text-white"
          style={{
            background: "rgba(31,58,46,0.98)",
            borderTop: "1px solid var(--header-2)",
            gridTemplateColumns: `repeat(${bottomNav.length + (overflowNav.length > 0 ? 1 : 0)}, 1fr)`,
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.5rem)",
            paddingTop: "0.25rem",
          }}
        >
          {bottomNav.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`m-${testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2.5 text-[10px] uppercase tracking-wider ${
                  isActive ? "text-[#D4AF37]" : "text-white/65"
                }`
              }
            >
              <Icon size={18} strokeWidth={1.5} />
              <span className="truncate max-w-full px-1">{label}</span>
            </NavLink>
          ))}
          {overflowNav.length > 0 && (
            <button
              type="button"
              data-testid="m-nav-more"
              onClick={() => setDrawerOpen(true)}
              className="flex flex-col items-center gap-1 py-2.5 text-[10px] uppercase tracking-wider text-white/65 hover:text-white"
            >
              <MoreHorizontal size={18} strokeWidth={1.5} />
              <span>Mehr</span>
            </button>
          )}
        </nav>
      )}

      <main
        className="flex-1 min-w-0 main-with-safe-area"
        style={{ background: "var(--bg)", color: "var(--text)" }}
      >
        {children}
      </main>
    </div>
  );
}
